/* Theme sync: 2026-09-03-base */
/*
  jrs-social-proof.js — Pause/Play control + reduced-motion handling for the
  standalone social-proof marquee (WCAG 2.2 SC 2.2.2).

  Vanilla ES5 IIFE. No dependencies, no EventBus, no network. Idempotent:
  safe to run multiple times and under the Shopify theme-editor lifecycle.
  The scrolling itself is pure CSS (compositor-driven keyframes) — do NOT drive
  the transform from JS per frame; iOS Safari blanks the composited layer after
  a few seconds when it is rewritten every frame. This only exposes an
  accessible control to pause/resume it and suppresses the control when there
  is nothing to pause (reduced-motion, or auto-scroll disabled in the schema).
*/
(function () {
  'use strict';

  var ATTR = 'data-jrs-social-proof';
  var READY = 'data-jrs-sp-ready';
  var PAUSE_LABEL = 'Pause reviews';
  var PLAY_LABEL = 'Play reviews';

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) {
      return false;
    }
  }

  function setPaused(root, toggle, paused) {
    if (paused) { root.classList.add('is-paused'); } else { root.classList.remove('is-paused'); }
    toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');

    var label = toggle.querySelector('[data-jrs-sp-toggle-label]');
    if (label) { label.textContent = paused ? PLAY_LABEL : PAUSE_LABEL; }

    var iconPause = toggle.querySelector('[data-jrs-sp-icon-pause]');
    var iconPlay = toggle.querySelector('[data-jrs-sp-icon-play]');
    if (iconPause) {
      if (paused) { iconPause.setAttribute('hidden', ''); } else { iconPause.removeAttribute('hidden'); }
    }
    if (iconPlay) {
      if (paused) { iconPlay.removeAttribute('hidden'); } else { iconPlay.setAttribute('hidden', ''); }
    }
  }

  function formatCount(n) {
    try {
      return Number(n).toLocaleString('en-GB');
    } catch (e) {
      return String(n);
    }
  }

  function animateCount(el, to, durationMs) {
    if (!el) { return; }
    var target = parseInt(to, 10);
    if (isNaN(target) || target < 0) {
      el.textContent = formatCount(to);
      return;
    }

    if (prefersReducedMotion() || durationMs <= 0) {
      el.textContent = formatCount(target);
      return;
    }

    var start = null;
    var from = 0;
    var duration = durationMs || 5000;
    var lastShown = -1;

    function frame(ts) {
      if (start === null) { start = ts; }
      var progress = Math.min(1, (ts - start) / duration);
      // Smoothstep ease-in-out — even pace through the middle, soft start/end.
      var eased = progress * progress * (3 - 2 * progress);
      var value = Math.round(from + (target - from) * eased);

      if (value !== lastShown) {
        lastShown = value;
        el.textContent = formatCount(value);
      }

      if (progress < 1) {
        el.__jrsSpRaf = window.requestAnimationFrame(frame);
      } else {
        el.textContent = formatCount(target);
        el.__jrsSpRaf = null;
      }
    }

    if (el.__jrsSpRaf) {
      window.cancelAnimationFrame(el.__jrsSpRaf);
    }
    el.__jrsSpRaf = window.requestAnimationFrame(frame);
  }

  function initCountUps(root) {
    var nodes = root.querySelectorAll('[data-jrs-sp-count]');
    if (!nodes.length) { return null; }

    var run = function () {
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.getAttribute('data-jrs-sp-count-done') === 'true') { continue; }
        el.setAttribute('data-jrs-sp-count-done', 'true');
        var to = el.getAttribute('data-jrs-sp-count-to');
        var duration = parseInt(el.getAttribute('data-jrs-sp-count-duration') || '5000', 10);
        animateCount(el, to, duration);
      }
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return null;
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          run();
          break;
        }
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.25 });

    observer.observe(root);
    return observer;
  }

  function initSection(root) {
    if (!root || root.getAttribute(READY) === 'true') { return; }
    root.setAttribute(READY, 'true');

    var countObserver = initCountUps(root);
    var toggle = root.querySelector('[data-jrs-sp-toggle]');
    var cleanups = [];

    if (countObserver) {
      cleanups.push(function () { countObserver.disconnect(); });
    }

    if (!toggle) {
      // No marquee (0 testimonials) — still run count-ups if present.
      root.__jrsSpCleanup = function () {
        for (var i = 0; i < cleanups.length; i++) { cleanups[i](); }
        root.removeAttribute(READY);
        root.__jrsSpCleanup = null;
      };
      return;
    }

    var autoscrollOff = root.getAttribute('data-autoscroll') === 'false';
    var isStatic = autoscrollOff || prefersReducedMotion();

    if (isStatic) {
      // Autoplay must not start: hide the control (nothing to pause). The CSS
      // freezes the rows and makes each row manually scrollable.
      toggle.hidden = true;
      root.setAttribute('data-jrs-sp-mode', 'static');
      root.__jrsSpCleanup = function () {
        for (var i = 0; i < cleanups.length; i++) { cleanups[i](); }
        root.removeAttribute(READY);
        root.removeAttribute('data-jrs-sp-mode');
        root.__jrsSpCleanup = null;
      };
      return;
    }

    root.setAttribute('data-jrs-sp-mode', 'auto');
    toggle.hidden = false;

    var onClick = function () {
      var nowPaused = !root.classList.contains('is-paused');
      setPaused(root, toggle, nowPaused);
    };
    toggle.addEventListener('click', onClick);
    cleanups.push(function () { toggle.removeEventListener('click', onClick); });

    root.__jrsSpCleanup = function () {
      for (var i = 0; i < cleanups.length; i++) { cleanups[i](); }
      root.removeAttribute(READY);
      root.removeAttribute('data-jrs-sp-mode');
      root.__jrsSpCleanup = null;
    };
  }

  function initAll(scope) {
    var ctx = scope || document;
    if (!ctx.querySelectorAll) { return; }
    var nodes = ctx.querySelectorAll('[' + ATTR + ']');
    for (var i = 0; i < nodes.length; i++) { initSection(nodes[i]); }
  }

  function cleanupSection(root) {
    if (!root) { return; }
    if (typeof root.__jrsSpCleanup === 'function') {
      root.__jrsSpCleanup();
    } else {
      root.removeAttribute(READY);
    }
    var countNodes = root.querySelectorAll('[data-jrs-sp-count]');
    for (var i = 0; i < countNodes.length; i++) {
      var el = countNodes[i];
      if (el.__jrsSpRaf) {
        window.cancelAnimationFrame(el.__jrsSpRaf);
        el.__jrsSpRaf = null;
      }
      el.removeAttribute('data-jrs-sp-count-done');
      el.textContent = '0';
    }
  }

  function rootFrom(target) {
    if (!target || !target.querySelector) { return null; }
    if (target.getAttribute && target.getAttribute(ATTR) !== null) { return target; }
    return target.querySelector('[' + ATTR + ']');
  }

  // --- Auto-init on load -------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // --- Theme-editor lifecycle (bind once, even if the tag is emitted per
  //     section instance) --------------------------------------------------
  if (!window.__jrsSocialProofBound) {
    window.__jrsSocialProofBound = true;

    document.addEventListener('shopify:section:load', function (e) {
      var root = rootFrom(e && e.target);
      if (root) { initSection(root); } else { initAll(); }
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var root = rootFrom(e && e.target);
      if (root) { cleanupSection(root); }
    });
  }

  // --- Test / diagnostic seam -------------------------------------------
  window.JRSSocialProof = {
    init: initAll,
    initSection: initSection,
    cleanupSection: cleanupSection,
    _prefersReducedMotion: prefersReducedMotion,
    _setPaused: setPaused
  };
})();
